import React, { useState } from 'react';
import { Container, Row, Col, Card, Button, Spinner } from 'react-bootstrap';
import { cacheService } from '../services/apiService';
import { usePermissions } from '../context/PermissionsContext';
import { useAuthToken } from '../hooks/useAuthToken';
import { useConfirm } from '../components/ConfirmDialog';
import { serverMessage } from '../components/PageState';
import { formatTime } from '../utils/format';

const CacheControls = () => {
  const { canEdit } = usePermissions();
  const ensureToken = useAuthToken();
  const { confirm, confirmDialog } = useConfirm();
  const [flushing, setFlushing] = useState(false);
  // The last flush's outcome, shown beside the button
  const [result, setResult] = useState(null);

  const handleFlush = async () => {
    const confirmed = await confirm({
      title: 'Flush the whole cache?',
      body: 'Clears every cached key. Pages load from the database and may be slower for a few minutes.',
      confirmLabel: 'Flush cache',
      variant: 'danger',
    });
    if (!confirmed) return;
    setFlushing(true);
    setResult(null);
    try {
      await ensureToken();
      await cacheService.flushAll();
      setResult({ ok: true, message: `Cleared at ${formatTime(new Date())}` });
    } catch (err) {
      console.error('Failed to flush cache:', err);
      setResult({ ok: false, message: serverMessage(err, 'Could not flush the cache. Try again.') });
    } finally {
      setFlushing(false);
    }
  };

  return (
    <Container fluid className="py-4">
      {confirmDialog}
      <Row className="mb-4">
        <Col>
          <h1>Cache controls</h1>
          <p className="text-muted mb-0">The shared Redis cache used by Team Today.</p>
        </Col>
      </Row>

      <Row>
        <Col lg={8}>
          <Card className="shadow-sm">
            <Card.Header className="bg-light">
              <h5 className="mb-0">Flush the cache</h5>
            </Card.Header>
            <Card.Body>
              <p>Clears every cached key, so the next requests read fresh data from the database.</p>
              <p className="text-muted">
                Saving company settings, importing users or adding a desk in this app already clears it. Use this when
                Team Today still shows stale data after another change, for example one made straight in the database.
              </p>
              {canEdit ? (
                <div className="d-flex flex-wrap align-items-center gap-3">
                  <Button variant="danger" onClick={handleFlush} disabled={flushing}>
                    {flushing && <Spinner animation="border" size="sm" className="me-2" aria-hidden="true" />}
                    {flushing ? 'Flushing...' : 'Flush cache'}
                  </Button>
                  {result && (
                    <span className={`save-status ${result.ok ? 'is-success' : 'is-error'}`} role="status">
                      {result.message}
                    </span>
                  )}
                </div>
              ) : (
                <p className="text-muted mb-0">
                  You have view-only access. Flushing the cache needs admin access.
                </p>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default CacheControls;
